import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { setIsLoginDialog } from '../../redux/reducers/loginSlice';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import axios from 'axios';
import { REQUEST_OTP_URL, VERIFY_OTP_URL } from '../../config/endpoints';
import ReactGA from 'react-ga4';

const OtpInputs = ({ length = 4, value, setValue, disabled }) => {
  const inputsRef = useRef([]);
  const wasDisabledRef = useRef(disabled);

  const handleChange = (idx, v) => {
    const next = v.replace(/\D/g, '').slice(-1);
    const arr = value.split('');
    arr[idx] = next || '';
    const newVal = arr.join('');
    setValue(newVal);
    if (next && idx < length - 1) inputsRef.current[idx + 1]?.focus();
  };

  // Auto-focus first box once enabled
  useEffect(() => {
    if (wasDisabledRef.current && !disabled) {
      setTimeout(() => inputsRef.current[0]?.focus(), 50);
    }
    wasDisabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    if (!wasDisabledRef.current && disabled) {
      const currentIndex = inputsRef.current.findIndex(input => input && input === document.activeElement);
      if (currentIndex !== -1) {
        inputsRef.current[currentIndex]?.blur();
      }
    }
    wasDisabledRef.current = disabled;
  }, [disabled]);

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) inputsRef.current[idx - 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (pasted.length) {
      e.preventDefault();
      const arr = value.split('');
      for (let i = 0; i < pasted.length; i++) arr[i] = pasted[i];
      setValue(arr.join('').padEnd(length, ''));
      const nextIndex = Math.min(pasted.length, length - 1);
      inputsRef.current[nextIndex]?.focus();
    }
  };

  return (
    <div className="flex gap-2" onPaste={handlePaste}>
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => (inputsRef.current[idx] = el)}
          value={value[idx] || ''}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          type="password"
          maxLength={1}
          inputMode="numeric"
          autoComplete="one-time-code"
          disabled={disabled}
          className={`w-[52px] h-12 text-center font-extrabold text-lg rounded-lg border-2 ${disabled
            ? 'bg-gray-100 border-gray-300'
            : 'bg-white border-gray-300 focus:border-emerald focus:ring-2 focus:ring-emerald/15 focus:outline-none'
            }`}
          style={{ letterSpacing: '1px' }}
        />
      ))}
    </div>
  );
};

// Inline brand icons
const GoogleIcon = (props) => (
  <svg viewBox="0 0 48 48" className="w-[18px] h-[18px]" {...props}>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.7 2.9l5.7-5.7C33.2 6.2 28.8 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.8-8 19.8-20 0-1.3-.1-2.2-.2-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.4 16.5 18.8 12 24 12c3 0 5.7 1.1 7.7 2.9l5.7-5.7C33.2 6.2 28.8 4 24 4 16 4 9 8.6 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.2-5.2l-6.1-5c-2 1.5-4.6 2.4-7.1 2.4-5.2 0-9.6-3.4-11.2-8l-6.6 5C9 39.4 15.9 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-0.8 2.3-2.2 4.2-4 5.6l6.1 5C40.9 35.6 44 30.6 44 24c0-1.2-0.1-2.3-0.4-3.5z" />
  </svg>
);

const MicrosoftIcon = (props) => (
  <svg viewBox="0 0 23 23" className="w-[18px] h-[18px]" {...props}>
    <path fill="#F35325" d="M1 1h10v10H1z" />
    <path fill="#81BC06" d="M12 1h10v10H12z" />
    <path fill="#05A6F0" d="M1 12h10v10H1z" />
    <path fill="#FFBA08" d="M12 12h10v10H12z" />
  </svg>
);

const AppleIcon = (props) => (
  <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" {...props}>
    <path fill="#000" d="M16.365 1.43c0 1.14-.46 2.24-1.21 3.04-.77.83-2.05 1.5-3.12 1.41-.14-1.09.46-2.29 1.17-3.03.81-.85 2.16-1.46 3.16-1.42zM20.67 17.06c-.59 1.37-2.57 4.75-4.56 4.79-1.07.02-1.9-.72-3.19-.72-1.29 0-2.19.7-3.19.74-2 .08-5.26-4.58-5.26-8.55 0-3.72 2.4-5.68 4.76-5.72 1.14-.02 2.22.78 2.98.78.74 0 2.1-.96 3.55-.82.6.03 2.29.24 3.37 1.85-2.88 1.76-2.44 6.16.54 7.65z" />
  </svg>
);

// Icons as SVG components
const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const PlayCircleIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
  </svg>
);

const AssignmentIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
  </svg>
);

export default function PreSignDialog({ open, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [role, setRole] = useState('player');
  const handleOpenSignIn = () => {
    if (typeof onClose === 'function') onClose();
    dispatch(setIsLoginDialog(true));
  };
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [otp, setOtp] = useState(''.padEnd(4, ''));
  const [terms, setTerms] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sent, setSent] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [phoneCountry, setPhoneCountry] = useState({ dialCode: '91', countryCode: 'in' });
  const [postVerify, setPostVerify] = useState(false);
  const enterDetailsBtnRef = useRef(null);
  const [verifyError, setVerifyError] = useState('');
  const [loginCount, setLoginCount] = useState(() => {
    try { return parseInt(localStorage.getItem('LOGIN_COUNT') || '0', 10) || 0; } catch { return 0; }
  });
  const [showExploreInfo, setShowExploreInfo] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);

  // Helpers to manage Explore attempts per verified phone
  const getExploreAttemptKey = () => {
    try {
      const cc = localStorage.getItem('PRESIGN_CC') || '';
      const pn = localStorage.getItem('PRESIGN_PHONE') || '';
      const key = `EXPLORE_ATTEMPTS_${cc}${pn}`;
      return key;
    } catch { return 'EXPLORE_ATTEMPTS_'; }
  };
  const getExploreAttempts = () => {
    try { return parseInt(localStorage.getItem(getExploreAttemptKey()) || '0', 10) || 0; } catch { return 0; }
  };
  const incExploreAttempts = () => {
    try {
      const key = getExploreAttemptKey();
      const next = (parseInt(localStorage.getItem(key) || '0', 10) || 0) + 1;
      localStorage.setItem(key, String(next));
      return next;
    } catch { return 0; }
  };

  // Manage dialog as its own history session
  const sessionPushedRef = useRef(false);
  const closedByBackRef = useRef(false);
  const stepPushCountRef = useRef(0);
  const popHandlerRef = useRef(null);
  const prevSentRef = useRef(false);
  const prevPostVerifyRef = useRef(false);

  useEffect(() => {
    const getCurrentStep = () => (postVerify ? 3 : sent ? 2 : 1);
    const handlePopState = (e) => {
      const state = e?.state || {};
      const targetStep = state && state.presignSession ? (state.step || 1) : null;
      const currentStep = getCurrentStep();

      if (targetStep !== null) {
        if (targetStep > currentStep) {
          try { window.history.go(-1); } catch { }
          return;
        }
        if (targetStep < currentStep) {
          if (targetStep === 2) {
            if (!sent) setSent(true);
            setPostVerify(false);
            return;
          }
          if (targetStep === 1) {
            setSent(false);
            setOtp(''.padEnd(4, ''));
            setVerifyError('');
            setPostVerify(false);
            return;
          }
        }
        return;
      }

      closedByBackRef.current = true;
      if (typeof onClose === 'function') onClose();
    };
    if (open) {
      try { window.history.pushState({ presignSession: true, step: 1 }, '', window.location.href); } catch { }
      sessionPushedRef.current = true;
      popHandlerRef.current = handlePopState;
      window.addEventListener('popstate', popHandlerRef.current);
      try {
        const stepStr = sessionStorage.getItem('openPreSignStep');
        if (stepStr === '3') {
          if (!sent) setSent(true);
          setPostVerify(true);
        }
        const wantExploreInfo = sessionStorage.getItem('openPreSignShowExploreInfo') === '1';
        if (wantExploreInfo) {
          if (!sent) setSent(true);
          setPostVerify(true);
          setShowExploreInfo(true);
        }
        sessionStorage.removeItem('openPreSignStep');
        sessionStorage.removeItem('openPreSignShowExploreInfo');
      } catch { }
    }
    return () => {
      if (popHandlerRef.current) window.removeEventListener('popstate', popHandlerRef.current);
    };
  }, [open, onClose, sent, postVerify]);

  useEffect(() => {
    if (open && !prevSentRef.current && sent) {
      try { window.history.pushState({ presignSession: true, step: 2 }, '', window.location.href); stepPushCountRef.current += 1; } catch { }
    }
    if (open && !prevPostVerifyRef.current && postVerify) {
      try { window.history.pushState({ presignSession: true, step: 3 }, '', window.location.href); stepPushCountRef.current += 1; } catch { }
    }
    prevSentRef.current = sent;
    prevPostVerifyRef.current = postVerify;
  }, [open, sent, postVerify]);

  useEffect(() => {
    if (!open && sessionPushedRef.current) {
      if (!closedByBackRef.current) {
        const steps = stepPushCountRef.current + 1;
        try { window.history.go(-steps); } catch { }
      }
      sessionPushedRef.current = false;
      closedByBackRef.current = false;
      stepPushCountRef.current = 0;
      prevSentRef.current = false;
      prevPostVerifyRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    try {
      if (open) ReactGA.event({ category: 'PreSign', action: 'presign_open' });
    } catch { }
  }, [open]);

  const wrappedOnClose = (...args) => {
    try { ReactGA.event({ category: 'PreSign', action: 'presign_close' }); } catch { }
    if (typeof onClose === 'function') onClose(...args);
  };

  // History handling for Explore Info sub-dialog
  useEffect(() => {
    const handlePopState = () => {
      setShowExploreInfo(false);
    };
    if (showExploreInfo) {
      try { window.history.pushState({ presignExploreInfo: true }, '', window.location.href); } catch { }
      window.addEventListener('popstate', handlePopState);
    }
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showExploreInfo]);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setEmailError('');
      setOtp(''.padEnd(4, ''));
      setTerms(false);
      setSending(false);
      setVerifying(false);
      setSent(false);
      setShowSuccess(false);
      setResendIn(0);
      setVerifyError('');
    }
  }, [open]);

  // Lock body scroll when dialog is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const isValidPhone = useMemo(() => {
    const digits = (email || '').replace(/\D/g, '');
    const dialLen = phoneCountry?.dialCode ? String(phoneCountry.dialCode).replace(/\D/g, '').length : 0;
    const nationalLen = Math.max(0, digits.length - dialLen);
    return nationalLen === 9 || nationalLen === 10;
  }, [email, phoneCountry]);
  const canSend = useMemo(() => isValidPhone && !sending, [isValidPhone, sending]);
  const canVerify = useMemo(() => sent && terms && otp.replace(/\D/g, '').length === 4 && !verifying, [sent, terms, otp, verifying]);

  const handleSendOtp = async () => {
    try { ReactGA.event({ category: 'PreSign', action: 'send_otp_click' }); } catch { }
    const valid = isValidPhone;
    if (!valid) {
      setEmailError('Please enter a valid mobile number');
      return;
    }
    setEmailError('');
    setSending(true);
    try {
      const digits = (email || '').replace(/\D/g, '');
      const dial = phoneCountry?.dialCode ? String(phoneCountry.dialCode).replace(/\D/g, '') : '91';
      const ccWithPlus = `+${dial}`;
      const national = digits.startsWith(dial) ? digits.slice(dial.length) : digits;
      const payload = { cc: ccWithPlus, number: national, role };
      console.debug('Request OTP payload', payload);
      const { data } = await axios.post(REQUEST_OTP_URL, payload, { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } });
      if (data?.success) {
        try { ReactGA.event({ category: 'PreSign', action: 'send_otp_success' }); } catch { }
        setSent(true);
        setResendIn(30);
      } else {
        setEmailError(data?.message || 'Failed to send OTP. Please try again.');
        try { ReactGA.event({ category: 'PreSign', action: 'send_otp_error', label: data?.message || '' }); } catch { }
      }
    } catch (err) {
      console.error('OTP request failed', err?.response?.data || err?.message || err);
      setEmailError(err?.response?.data?.message || 'Failed to send OTP. Please try again.');
      try { ReactGA.event({ category: 'PreSign', action: 'send_otp_error', label: err?.message || 'network' }); } catch { }
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    try { ReactGA.event({ category: 'PreSign', action: 'verify_click' }); } catch { }
    if (!canVerify) return;
    setVerifying(true);
    setVerifyError('');

    const previousRole = localStorage.getItem('PRESIGN_ROLE');
    const currentRole = role;

    if (previousRole && previousRole !== currentRole) {
      localStorage.setItem('ROLE_SWITCH_DETECTED', '1');
      localStorage.setItem('PREVIOUS_ROLE_FOR_SWITCH', previousRole);
      try { ReactGA.event({ category: 'PreSign', action: 'role_switch_attempt', label: `${previousRole}->${currentRole}` }); } catch { }
    }

    try {
      const dial = phoneCountry?.dialCode ? String(phoneCountry.dialCode).replace(/\D/g, '') : '91';
      const ccWithPlus = `+${dial}`;
      const digitsPhone = (email || '').replace(/\D/g, '');
      const national = digitsPhone.startsWith(dial) ? digitsPhone.slice(dial.length) : digitsPhone;
      const otpDigits = otp.replace(/\D/g, '');

      const payload = { cc: ccWithPlus, number: national, otp: otpDigits };
      console.debug('Verify OTP payload', payload);
      const { data } = await axios.post(VERIFY_OTP_URL, payload, { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } });
      if (!data?.success) {
        setVerifyError(data?.message || 'Invalid or expired OTP. Please try again.');
        setVerifying(false);
        try { ReactGA.event({ category: 'PreSign', action: 'verify_error', label: data?.message || '' }); } catch { }
        return;
      }

      try {
        if (data?.role) {
          localStorage.setItem('PRESIGN_ROLE', data.role);
        }
        if (typeof data?.loginCount === 'number') {
          localStorage.setItem('LOGIN_COUNT', String(data.loginCount));
          setLoginCount(Number(data.loginCount));
        }
        if (data?.number) localStorage.setItem('PRESIGN_PHONE', String(data.number));
        localStorage.setItem('PRESIGN_CC', ccWithPlus);
        try {
          const newKey = `${ccWithPlus}${national}`;
          const prevKey = sessionStorage.getItem('PG_TRIAL_PHONE_KEY') || '';
          if (newKey && newKey !== prevKey) {
            sessionStorage.setItem('PG_TRIAL_ASK_COUNT', '0');
            sessionStorage.setItem('PG_TRIAL_PHONE_KEY', newKey);
          }
        } catch { }
      } catch { }

      setShowSuccess(true);
      try { ReactGA.event({ category: 'PreSign', action: 'verify_success' }); } catch { }
      setTimeout(() => {
        setShowSuccess(false);
        try {
          if (!localStorage.getItem('PRESIGN_ROLE')) localStorage.setItem('PRESIGN_ROLE', role);
          if (!localStorage.getItem('PRESIGN_PHONE') && national) localStorage.setItem('PRESIGN_PHONE', national);
        } catch { }
        setPostVerify(true);
      }, 1200);
    } catch (err) {
      console.error('OTP verification failed', err?.response?.data || err?.message || err);
      setVerifyError(err?.response?.data?.message || 'Invalid or expired OTP. Please try again.');
      try { ReactGA.event({ category: 'PreSign', action: 'verify_error', label: err?.message || 'network' }); } catch { }
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  useEffect(() => {
    if (postVerify) {
      setTimeout(() => enterDetailsBtnRef.current?.focus(), 50);
    }
  }, [postVerify]);

  const handleKeyDownDialog = (e) => {
    if (e.key === 'Enter') {
      if (!sent && canSend && resendIn === 0) handleSendOtp();
      else if (canVerify) handleVerify();
    }
  };

  if (!open) return null;

  const dialogContent = (
    <>
      {/* Main Dialog */}
      <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center p-4 z-[1300] overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-[560px] relative"
            style={{
              background: 'radial-gradient(1200px 500px at 10% -10%, rgba(0,177,64,0.10) 0%, rgba(0,177,64,0.00) 60%), radial-gradient(1200px 500px at 110% 110%, rgba(0,177,64,0.10) 0%, rgba(0,177,64,0.00) 60%), linear-gradient(180deg, #ffffff 0%, #f7fff9 100%)',
              border: '1px solid rgba(0,177,64,0.14)',
              borderRadius: '24px',
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <div className="relative flex items-center justify-center px-4 pt-3.5">
              <div className="flex flex-col items-center">
                <div className="text-3xl font-medium text-[#0a3d2a] tracking-wide mt-1">TRY A DEMO!</div>
                <hr className="w-full my-2 border-[#6c8277]" />
              </div>
              <button
                onClick={wrappedOnClose}
                className="absolute right-2 top-2 w-8 h-8 flex items-center justify-center rounded-full text-[#243d33] hover:bg-gray-100 transition-colors"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Content */}
            <div className="pt-2.5 pb-4 px-4" onKeyDown={handleKeyDownDialog}>
              {/* Role selection */}
              {!sent && (
                <div className="flex items-center justify-center gap-3 mb-2">
                  <div className="text-[#0a3d2a] mr-1 text-sm font-extrabold">Register As:</div>
                  {[
                    { key: 'player', label: 'Player' },
                    { key: 'coach', label: 'Coach' },
                    { key: 'organization', label: 'Organization' }
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => { try { if (role !== opt.key) ReactGA.event({ category: 'PreSign', action: 'role_change', label: opt.key }); } catch { } setRole(opt.key) }}
                      disabled={sent}
                      className={`text-xs font-extrabold rounded-full px-3 py-1.5 transition-all ${role === opt.key
                        ? 'bg-gradient-to-br from-[#026B33] to-[#026B33] text-white border-transparent'
                        : 'border border-black/15 text-[#264339] hover:border-[#026B33] hover:text-[#026B33] hover:bg-[#026B33]/5'
                        } ${sent ? 'opacity-100 cursor-not-allowed' : ''}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              {sent && !postVerify && (
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="text-[#0a3d2a] text-sm font-extrabold">Registering as:</div>
                  <div className="text-[#016a33] text-sm font-black px-2 py-1 rounded-full bg-[#026B33]/8 border border-[#026B33]/25">
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </div>
                </div>
              )}

              {/* Intro copy */}
              {!sent ? (
                <>
                  <div className="text-center text-[#2a473c] text-lg mb-0.5 mt-3">
                    Enter your mobile number to get started
                  </div>
                  <div className="text-center text-[#70877c] text-sm mb-13">
                    We'll send a one‑time code. We never share your number.
                  </div>
                </>
              ) : sent && !postVerify ? (
                <div className="text-center text-[#5a6d65] text-xs mt-3 mb-2">
                  OTP has been sent to (+{email}).{' '}
                  <button
                    onClick={() => { setSent(false); setOtp(''.padEnd(4, '')); }}
                    className="text-[#026B33] font-semibold hover:underline"
                  >
                    Edit
                  </button>
                </div>
              ) : null}

              <div className="flex flex-col gap-3 mt-3">
                {/* Phone Input */}
                {!sent && (
                  <div>
                    <PhoneInput
                      country={phoneCountry?.countryCode || 'in'}
                      value={email}
                      onChange={(value, country) => {
                        setEmail(value);
                        setPhoneCountry(country);
                        if (emailError) setEmailError('');
                      }}
                      enableSearch
                      inputProps={{
                        name: 'phone',
                        required: true,
                        autoComplete: 'tel',
                        'aria-label': 'Mobile phone number'
                      }}
                      placeholder="Enter your mobile number"
                      containerStyle={{ width: '100%' }}
                      inputStyle={{ width: '100%', height: 48, borderRadius: 10, fontSize: 16 }}
                      buttonStyle={{ borderTopLeftRadius: 10, borderBottomLeftRadius: 10 }}
                    />
                    {Boolean(emailError) && (
                      <div className="text-red-600 text-xs mt-1">{emailError}</div>
                    )}
                  </div>
                )}

                {/* Consent checkbox */}
                {!sent && (
                  <label className="flex items-start mt-0 mb-1 ml-10">
                    <input
                      type="checkbox"
                      checked={terms}
                      onChange={(e) => setTerms(e.target.checked)}
                      className="mt-0.5 mr-2 w-4 h-4 text-emerald border-gray-300 rounded focus:ring-emerald"
                    />
                    <span className="text-[#2f4f3e] text-xs leading-tight">
                      By signing up, you are accepting our{' '}
                      <Link to="/term_service" className="text-emerald font-bold">Terms and Conditions</Link>.
                    </span>
                  </label>
                )}

                {/* Send OTP Button */}
                {!sent && (
                  <div className="flex items-center gap-2 mb-0.5">
                    <button
                      onClick={handleSendOtp}
                      disabled={!canSend || resendIn > 0 || !terms}
                      className={`w-full py-2.5 text-base font-black rounded-lg transition-all ${!canSend || resendIn > 0 || !terms
                        ? 'bg-[#c9d2cf] text-white cursor-not-allowed'
                        : 'bg-gradient-to-br from-[#026B33] to-[#026B33] text-white hover:from-[#005a00] hover:to-[#00a23a] shadow-lg'
                        }`}
                    >
                      {sending ? 'Sending…' : 'Send OTP'}
                    </button>
                  </div>
                )}

                {/* OTP Input */}
                {sent && !postVerify && (
                  <div className="flex items-center justify-center mt-2">
                    <OtpInputs length={4} value={otp} setValue={setOtp} disabled={!sent} />
                  </div>
                )}
                {sent && !postVerify && verifyError && (
                  <div className="text-center text-[#c62828] text-xs mt-2">{verifyError}</div>
                )}
                {sent && !postVerify && (
                  <>
                    <div className="text-center text-[#5a6d65] text-sm mt-2">
                      Didn't get a code?
                      <button
                        onClick={handleSendOtp}
                        disabled={resendIn > 0}
                        className={`ml-1 text-[#026B33] font-semibold ${resendIn > 0 ? 'opacity-50 cursor-not-allowed' : 'hover:underline'}`}
                      >
                        {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend'}
                      </button>
                    </div>
                    <button
                      onClick={handleVerify}
                      disabled={!canVerify}
                      className={`mt-1 py-2.5 font-black rounded-lg transition-all ${!canVerify
                        ? 'bg-[#c9d2cf] text-white cursor-not-allowed'
                        : 'bg-gradient-to-br from-[#026B33] to-[#026B33] text-white hover:from-[#005a00] hover:to-[#00a23a] shadow-lg'
                        }`}
                    >
                      {verifying ? 'Verifying…' : 'Verify & Continue'}
                    </button>
                  </>
                )}

                {/* Post-verify options */}
                {postVerify && (
                  <>
                    <hr className="my-2.5" />
                    <div className="text-center mb-2">
                      <div className="text-xl font-extrabold text-[#0a3d2a]">
                        {loginCount > 1 ? 'Welcome Back! What would you like to do?' : 'You\'re in! What would you like to do?'}
                      </div>
                      <div className="text-sm text-[#6b8478]">Choose an option to continue</div>
                    </div>
                    <div className="flex flex-row gap-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          try { ReactGA.event({ category: 'PreSign', action: 'explore_products_click' }); } catch { }
                          closedByBackRef.current = true; // Prevent history rollback
                          if (typeof onClose === 'function') onClose();
                          navigate('/explore');
                        }}
                        className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-extrabold rounded-lg text-[#026B33] border border-[#026B33] bg-[#eef2f1] hover:bg-[#026B33]/5 transition-colors min-h-[50px]"
                      >
                        <PlayCircleIcon /> Explore Our Products
                      </button>
                      <button
                        ref={enterDetailsBtnRef}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          try { localStorage.setItem('SIGNUP_PENDING', '1'); } catch { }
                          try { dispatch(setIsLoginDialog(false)); } catch { }
                          try { ReactGA.event({ category: 'PreSign', action: 'presign_signup_click' }); } catch { }
                          closedByBackRef.current = true; // Prevent history rollback
                          if (typeof onClose === 'function') onClose();
                          try {
                            sessionStorage.setItem('openPreSignOnReturn', '1');
                            sessionStorage.setItem('openPreSignStep', '3');
                            sessionStorage.setItem('fromPreSignNav', '1');
                          } catch { }
                          navigate('/signup');
                        }}
                        className="flex-1 flex items-center justify-center gap-2 py-3 text-base font-black rounded-lg bg-[#026B33] text-white hover:bg-[#026B33]/90 transition-colors min-h-[50px] shadow-none"
                      >
                        <AssignmentIcon /> Sign Up
                      </button>
                    </div>
                  </>
                )}

                {/* Already have account */}
                {!postVerify && (
                  <div className="mt-0 text-center text-sm text-[#2a473c]">
                    Already have an account?
                    <button
                      onClick={handleOpenSignIn}
                      className="ml-1 text-[#007a35] font-extrabold hover:underline"
                    >
                      Sign in
                    </button>
                  </div>
                )}

                {showSuccess && (
                  <div className="flex items-center justify-center gap-2 text-[#0a3d2a] font-extrabold mt-3">
                    <CheckCircleIcon /> Verified!
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Explore Info Dialog */}
      {showExploreInfo && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center p-4 z-[1350]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-[560px] bg-gradient-to-b from-white to-[#f6fff9] rounded-3xl border border-emerald/15 shadow-2xl overflow-hidden"
          >
            <div className="relative px-6 pt-6 pb-4">
              <button
                onClick={() => {
                  setShowExploreInfo(false);
                  try { if (window.history.state && window.history.state.presignExploreInfo) window.history.back(); } catch { }
                  if (typeof onClose === 'function') onClose();
                }}
                className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
              >
                <CloseIcon />
              </button>
              <div className="text-center">
                <div className="text-3xl font-black text-[#026B33] mb-2">SmartInsights</div>
                <div className="text-sm text-gray-600 font-medium">
                  An AI‑powered, cloud‑based personal journal for athletes
                </div>
              </div>
            </div>
            <div className="pb-6 px-6">
              <div className="text-gray-700 text-base leading-relaxed mb-4 font-semibold text-center">
                Dive into the playground to experience how our AI analyzes match videos and explore features:
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { src: '/playground/sample/heatmap.png', title: 'Heatmap' },
                  { src: '/playground/sample/tracking.png', title: 'Tracking' },
                  { src: '/playground/sample/occupancy.png', title: 'Occupancy' },
                ].map((card, idx) => (
                  <div
                    key={idx}
                    className="rounded-2xl px-3 py-3 bg-white border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 flex flex-col items-center"
                  >
                    <div className="w-full rounded-xl p-2.5 bg-gray-100">
                      <div className="relative w-full pt-[170%] rounded-lg overflow-hidden bg-gradient-to-b from-[#0b6a35] to-[#09532a]">
                        <img src={card.src} alt={card.title} className="absolute inset-0 w-full h-full object-cover" />
                      </div>
                    </div>
                    <div className="mt-2.5 px-3 py-1.5 rounded-full bg-gray-100 font-semibold text-[#026B33] text-xs">
                      {card.title}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    try {
                      const current = getExploreAttempts();
                      if ((current || 0) >= 5) {
                        setShowLimitDialog(true);
                        return;
                      }
                    } catch { }

                    try {
                      sessionStorage.setItem('openPreSignOnReturn', '1');
                      sessionStorage.setItem('openPreSignStep', '3');
                      sessionStorage.setItem('PG_TRIAL_MODE', '1');
                      sessionStorage.setItem('PG_TRIAL_START', String(Date.now()));
                      try {
                        const digits = (email || '').replace(/\D/g, '');
                        const dial = phoneCountry?.dialCode ? String(phoneCountry.dialCode).replace(/\D/g, '') : '91';
                        const ccWithPlusBtn = `+${dial}`;
                        const nationalBtn = digits.startsWith(dial) ? digits.slice(dial.length) : digits;
                        if (nationalBtn) sessionStorage.setItem('PG_TRIAL_PHONE_KEY', `${ccWithPlusBtn}${nationalBtn}`);
                      } catch { }
                      try {
                        const savedSel = localStorage.getItem('PLAYGROUND_SELECTED_VIDEO');
                        if (!savedSel || savedSel === 'none') {
                          sessionStorage.setItem('PG_TRIAL_FORCE_RANDOM', '1');
                        } else {
                          sessionStorage.removeItem('PG_TRIAL_FORCE_RANDOM');
                        }
                      } catch { }
                    } catch { }
                    incExploreAttempts();
                    if (typeof onClose === 'function') onClose();
                    setShowExploreInfo(false);
                    navigate('/SmartInsights');
                  }}
                  className="mt-5 px-8 py-3 text-base font-black rounded-full bg-gradient-to-br from-[#026B33] to-[#00b140] text-white shadow-lg shadow-emerald/30 hover:shadow-xl hover:from-[#026B33] hover:to-[#00923a] transition-all"
                >
                  Explore Now
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Explore Limit Dialog */}
      {showLimitDialog && (
        <div className="fixed inset-0 bg-black/45 backdrop-blur-sm flex items-center justify-center p-4 z-[1400]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-[560px] bg-gradient-to-b from-white to-[#f7fff9] rounded-3xl border border-emerald/15 overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-center gap-2.5 px-3 py-1.5 mx-auto mb-2 w-fit rounded-full bg-emerald/8 border border-emerald/25 text-[#026B33] font-extrabold">
                <img src="/playground/shuttle.png" alt="Shuttlecock" className="w-5 h-5" />
                <span>SmartInsights Trial</span>
              </div>
              <div className="text-center">
                <div className="text-2xl font-black text-[#0a3d2a]">Reached Limit</div>
                <div className="text-[#2a473c] mt-1">You have used 5 trial visits. Please Sign Up to explore more.</div>
              </div>
              <hr className="my-2.5" />
              <div className="flex gap-2 justify-center pb-1">
                <button
                  onClick={() => setShowLimitDialog(false)}
                  className="px-4 py-2 text-[#026B33] font-extrabold hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    try {
                      sessionStorage.setItem('openPreSignOnReturn', '1');
                      sessionStorage.setItem('openPreSignStep', '3');
                    } catch { }
                    if (typeof onClose === 'function') onClose();
                    setShowLimitDialog(false);
                    navigate('/signup');
                  }}
                  className="px-4 py-2 bg-emerald text-white font-black rounded-lg hover:bg-emerald-600 transition-colors"
                >
                  Sign Up
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );

  return ReactDOM.createPortal(dialogContent, document.body);
}
